/**
 * Cart Manager — The Spanish Alchemist
 * ─────────────────────────────────────────────────────────────────────────────
 * Single file replacing upsell-engine.js + gift-tier.js.
 *
 * Source of truth: /cart.js — fetched fresh on every sync.
 * No sessionStorage. No window flags. No cross-file events.
 *
 * Responsibilities:
 *   1. Auto-apply NEW20 discount to every cart with non-gift items
 *   2. Reconcile gift line items — 1 gift with every purchase, no tier conditions
 *   3. Render gift slots (with swap), discount notice, totals
 *   4. Checkout button goes straight to /checkout
 *
 * Discount rule: NEW20 is applied silently on every sync when the cart
 * has at least one non-gift item and no other code is already active.
 * It is removed automatically when the cart is emptied.
 * A different manually-entered code (e.g. influencer code) takes priority —
 * NEW20 will not overwrite it.
 *
 * Event contract (Maestroo native):
 *   Listens: cart:change (document), cart-drawer:refreshed (document)
 *   Uses:    cart:refresh on documentElement → triggers Maestroo drawer redraw
 */

(function () {
  'use strict';

  /* ═══════════════════════════════════════════════════════════════════════════
     CONFIG
  ═══════════════════════════════════════════════════════════════════════════ */

  var ROUTINE15_CODE    = 'ROUTINE15';
  var ROUTINE15_PCT     = 0.15;
  var MIN_PURCHASE_CENTS = 5000; // €50 — minimum for ROUTINE15 to apply
  var SHIPPING_CENTS    = 599;
  var SHIPPING_THRESHOLD = 5000; // €50

  /* Auto-apply coupon — silently applied to every non-empty cart */
  var AUTO_COUPON = 'NEW20';

  /**
   * Gift logic — 1 free gift per order, variant resolved from cart contents.
   *   AA Serum  → AA Cream mini      AA Cream → AA Serum mini
   *   HP Serum  → HP Cream mini      HP Cream → HP Serum mini
   *   AA Routine → HP Serum mini     HP Routine → AA Serum mini
   *   Other     → AA Serum mini (fallback)
   * Priority: lowest-priced non-gift item triggers first.
   */
  var GIFT_VARIANTS = {
    AA_SERUM : 53624342053191,
    AA_CREAM : 53624388026695,
    HP_SERUM : 53624359256391,
    HP_CREAM : 53624401396039,
  };

  var GIFT_VARIANT_IDS = {};
  Object.keys(GIFT_VARIANTS).forEach(function (k) { GIFT_VARIANT_IDS[GIFT_VARIANTS[k]] = true; });

  var GIFT_FALLBACK_ORDER = [
    GIFT_VARIANTS.AA_SERUM, GIFT_VARIANTS.HP_SERUM,
    GIFT_VARIANTS.AA_CREAM, GIFT_VARIANTS.HP_CREAM,
  ];

  var GIFT_TRIGGER_MAP = (function () {
    var m = {};
    [10635980144967, 10635980177735, 10496187957575].forEach(function (id) { m[id] = GIFT_VARIANTS.AA_CREAM; });
    [10635980210503, 10496209977671, 10496210993479].forEach(function (id) { m[id] = GIFT_VARIANTS.AA_SERUM; });
    [10635980276039, 10635980308807, 10496193233223].forEach(function (id) { m[id] = GIFT_VARIANTS.HP_CREAM; });
    [10635980341575, 10635980407111, 10496214303047].forEach(function (id) { m[id] = GIFT_VARIANTS.HP_SERUM; });
    [10635980734791, 10635980767559, 10635980833095,
     10635980964167, 10635980996935, 10635981029703].forEach(function (id) { m[id] = GIFT_VARIANTS.HP_SERUM; });
    [10635980865863, 10635980898631, 10635980931399,
     10635981062471, 10635981095239, 10635981128007].forEach(function (id) { m[id] = GIFT_VARIANTS.AA_SERUM; });
    return m;
  }());

  function resolveGiftVariant(cart) {
    var items = cart.items
      .filter(function (i) { return !isGiftItem(i); })
      .slice().sort(function (a, b) { return a.price - b.price; });
    for (var i = 0; i < items.length; i++) {
      var gv = GIFT_TRIGGER_MAP[items[i].product_id];
      if (gv) return gv;
    }
    return GIFT_FALLBACK_ORDER[0];
  }


  // Tiers kept for reference but not used — 1 gift with every purchase
  var TIERS = [
    { min: 7500, count: 3 },
    { min: 6500, count: 2 },
    { min: 5500, count: 1 },
    { min: 0,    count: 0 },
  ];

  // Milestones kept for reference but progress bar is paused
  var MILESTONES = [
    { threshold: 5000, label: 'Free Shipping' },
    { threshold: 5500, label: 'Gift 1'        },
    { threshold: 6500, label: 'Gift 2'        },
    { threshold: 7500, label: 'Gift 3'        },
  ];

  /**
   * All qualifying products for ROUTINE15.
   * handle → { line, form, skin, price (cents), name }
   */
  var PRODUCT_MAP = {
    // ── AA Serums ─────────────────────────────────────────────────────────
    'ageless-serum-oily-combination': { line: 'aa', form: 'serum', skin: 'oily',      price: 3900, name: 'Ageless Lift Serum'         },
    'ageless-serum-dry':              { line: 'aa', form: 'serum', skin: 'dry',       price: 3900, name: 'Ageless Lift Serum'         },
    'ageless-serum-sensitive':        { line: 'aa', form: 'serum', skin: 'sensitive', price: 3900, name: 'Ageless Lift Serum'         },
    // ── AA Creams ─────────────────────────────────────────────────────────
    'ageless-moisturizer-oily-combination': { line: 'aa', form: 'cream', skin: 'oily',      price: 3100, name: 'Ageless Lift Moisturizer'     },
    'ageless-moisturizer-dry':              { line: 'aa', form: 'cream', skin: 'dry',       price: 3100, name: 'Ageless Lift Moisturizer'     },
    'ageless-moisturizer-sensitive':        { line: 'aa', form: 'cream', skin: 'sensitive', price: 3100, name: 'Ageless Lift Moisturizer'     },
    // ── HP Serums ─────────────────────────────────────────────────────────
    'even-tone-serum-oily-combination': { line: 'hp', form: 'serum', skin: 'oily',      price: 4200, name: 'Even-Tone Glow Serum'        },
    'even-tone-serum-dry':              { line: 'hp', form: 'serum', skin: 'dry',       price: 4200, name: 'Even-Tone Glow Serum'        },
    'even-tone-serum-sensitive':        { line: 'hp', form: 'serum', skin: 'sensitive', price: 4200, name: 'Even-Tone Glow Serum'        },
    // ── HP Creams ─────────────────────────────────────────────────────────
    'even-tone-moisturizer-oily-combination': { line: 'hp', form: 'cream', skin: 'oily',      price: 3200, name: 'Even-Tone Glow Moisturizer'  },
    'even-tone-moisturizer-dry':              { line: 'hp', form: 'cream', skin: 'dry',       price: 3200, name: 'Even-Tone Glow Moisturizer'  },
    'even-tone-moisturizer-sensitive':        { line: 'hp', form: 'cream', skin: 'sensitive', price: 3200, name: 'Even-Tone Glow Moisturizer'  },
    // ── AC Serums ─────────────────────────────────────────────────────────
    'bye-breakouts-serum-oily-combination': { line: 'ac', form: 'serum', skin: 'oily',      price: 3300, name: 'Bye Breakouts Serum'         },
    'bye-breakouts-serum-dry':              { line: 'ac', form: 'serum', skin: 'dry',       price: 3300, name: 'Bye Breakouts Serum'         },
    'bye-breakouts-serum-sensitive':        { line: 'ac', form: 'serum', skin: 'sensitive', price: 3300, name: 'Bye Breakouts Serum'         },
    // ── AC Creams ─────────────────────────────────────────────────────────
    'bye-breakouts-moisturizer-oily-combination': { line: 'ac', form: 'cream', skin: 'oily',      price: 2500, name: 'Bye Breakouts Moisturizer'   },
    'bye-breakouts-moisturizer-dry':              { line: 'ac', form: 'cream', skin: 'dry',       price: 2500, name: 'Bye Breakouts Moisturizer'   },
    'bye-breakouts-moisturizer-sensitive':        { line: 'ac', form: 'cream', skin: 'sensitive', price: 2500, name: 'Bye Breakouts Moisturizer'   },
  };

  var LINE_NAMES = { aa: 'Ageless Lift', hp: 'Even-Tone Glow', ac: 'Bye Breakouts' };
  var SKIN_NAMES = { oily: 'Oily/Combination', dry: 'Dry', sensitive: 'Sensitive' };

  // Build companion map: 'aa-oily' → { serum: handle, cream: handle }
  var COMPANION_MAP = {};
  Object.keys(PRODUCT_MAP).forEach(function (handle) {
    var p   = PRODUCT_MAP[handle];
    var key = p.line + '-' + p.skin;
    if (!COMPANION_MAP[key]) COMPANION_MAP[key] = {};
    COMPANION_MAP[key][p.form] = handle;
  });

  /* ═══════════════════════════════════════════════════════════════════════════
     STATE  — minimal, never persisted
  ═══════════════════════════════════════════════════════════════════════════ */

  var isReconciling  = false; // prevents concurrent gift reconciliation
  var refreshPending = false; // true while we're waiting for Maestroo to re-render
  var variantCache   = {};    // handle → variantId (for upsell ATC)
  var imageCache     = {};    // handle → image URL  (for upsell card)

  /* ═══════════════════════════════════════════════════════════════════════════
     HELPERS
  ═══════════════════════════════════════════════════════════════════════════ */

  function extractHandle(item) {
    var m = item.url && item.url.match(/\/products\/([^?#]+)/);
    return m ? m[1] : null;
  }

  function isGiftItem(item) {
    /* Travel minis are purchasable — never classify as free gift regardless of variant ID */
    if (item.properties && item.properties['_travel_mini'] === '1') return false;
    if (item.properties && item.properties._is_gift === 'true') return true;
    return !!GIFT_VARIANT_IDS[item.variant_id];
  }

  /**
   * Detects whether the cart contains a complete serum+cream pair
   * (same line, same skin type). Computed fresh from cart data every time.
   */
  function detectPair(cart) {
    var lineSkInCart = {};
    cart.items.forEach(function (item) {
      if (isGiftItem(item)) return;
      var handle = extractHandle(item);
      if (!handle || !PRODUCT_MAP[handle]) return;
      var p   = PRODUCT_MAP[handle];
      var key = p.line + '-' + p.skin;
      if (!lineSkInCart[key]) lineSkInCart[key] = {};
      lineSkInCart[key][p.form] = true;
    });
    return Object.keys(lineSkInCart).some(function (key) {
      return lineSkInCart[key].serum && lineSkInCart[key].cream;
    });
  }

  /**
   * ROUTINE15 saving in cents, calculated from original_line_price.
   * Independent of what Shopify has in cart.total_price.
   */
  function calcRoutine15Saving(cart) {
    var qualifyingCents = 0;
    cart.items.forEach(function (item) {
      if (isGiftItem(item)) return;
      var handle = extractHandle(item);
      if (handle && PRODUCT_MAP[handle]) {
        qualifyingCents += item.original_line_price;
      }
    });
    var rawSubtotal = cart.items.reduce(function (sum, item) {
      return isGiftItem(item) ? sum : sum + item.original_line_price;
    }, 0);
    if (rawSubtotal < MIN_PURCHASE_CENTS) return 0;
    return Math.round(qualifyingCents * ROUTINE15_PCT);
  }

  /**
   * Effective subtotal for gift tier calculation.
   * Stacks discounts correctly:
   *   1. ROUTINE15 (15% on qualifying items if pair detected)
   *   2. Manual code applied on the post-ROUTINE15 value
   */
  function calcEffectiveSubtotal(cart, pairDetected) {
    var rawSubtotal     = cart.items.reduce(function (sum, item) {
      return isGiftItem(item) ? sum : sum + item.original_line_price;
    }, 0);
    var routine15Saving = pairDetected ? calcRoutine15Saving(cart) : 0;
    var postRoutine15   = rawSubtotal - routine15Saving;

    var manualCode = getManualCode(cart);
    if (manualCode && cart.total_discount > 0 && rawSubtotal > 0) {
      var manualPct = cart.total_discount / rawSubtotal;
      return Math.round(postRoutine15 * (1 - manualPct));
    }
    return postRoutine15;
  }

  function getTierCount(subtotalCents) {
    for (var i = 0; i < TIERS.length; i++) {
      if (subtotalCents >= TIERS[i].min) return TIERS[i].count;
    }
    return 0;
  }

  function getProgressState(subtotal) {
    var lastThresh = MILESTONES[MILESTONES.length - 1].threshold;
    if (subtotal >= lastThresh) {
      var giftCount = getTierCount(subtotal);
      return {
        progress: 1,
        message: '🎉 Free shipping + all <strong>' + giftCount + ' free gifts</strong> unlocked!',
      };
    }
    var activeIdx  = 0;
    for (var i = 0; i < MILESTONES.length; i++) {
      if (subtotal < MILESTONES[i].threshold) { activeIdx = i; break; }
    }
    var prevThresh = activeIdx === 0 ? 0 : MILESTONES[activeIdx - 1].threshold;
    var nextThresh = MILESTONES[activeIdx].threshold;
    var progress   = Math.min(1, Math.max(0, (subtotal - prevThresh) / (nextThresh - prevThresh)));
    var needed     = formatMoney(nextThresh - subtotal);
    var giftCount  = getTierCount(subtotal);
    var hasShipping = subtotal >= SHIPPING_THRESHOLD;
    var message;
    if (!hasShipping) {
      message = 'Add <strong>' + needed + '</strong> more for <strong>free shipping</strong> 🚚';
    } else if (giftCount === 0) {
      message = '🚚 Free shipping unlocked! Add <strong>' + needed + '</strong> more for a <strong>free gift</strong> 🎁';
    } else {
      message = 'Add <strong>' + needed + '</strong> more to unlock your <strong>' + ordinal(giftCount + 1) + ' free gift</strong> 🎁';
    }
    return { progress: progress, message: message };
  }

  /**
   * Returns whether a manual (non-ROUTINE15) discount code is applied in the cart.
   */
  function getManualCode(cart) {
    if (!cart.discount_codes || !cart.discount_codes.length) return null;
    for (var i = 0; i < cart.discount_codes.length; i++) {
      var d = cart.discount_codes[i];
      if (d.code !== ROUTINE15_CODE && d.applicable) return d.code;
    }
    return null;
  }

  function isRoutine15InCart(cart) {
    if (!cart.discount_codes) return false;
    return cart.discount_codes.some(function (d) {
      return d.code === ROUTINE15_CODE && d.applicable;
    });
  }

  function formatMoney(cents) {
    var euros = cents / 100;
    return '€' + (euros % 1 === 0 ? euros.toFixed(0) : euros.toFixed(2));
  }

  function ordinal(n) {
    var s = ['th', 'st', 'nd', 'rd'];
    var v = n % 100;
    return n + (s[(v - 20) % 10] || s[v] || s[0]);
  }

  function esc(str) {
    return String(str)
      .replace(/&/g, '&amp;').replace(/</g, '&lt;')
      .replace(/>/g, '&gt;').replace(/"/g, '&quot;');
  }

  function escAttr(str) { return String(str).replace(/"/g, '&quot;'); }

  /* ═══════════════════════════════════════════════════════════════════════════
     CART API CALLS
  ═══════════════════════════════════════════════════════════════════════════ */

  function fetchCart() {
    return fetch(Shopify.routes.root + 'cart.js', { cache: 'no-store' }).then(function (r) { return r.json(); });
  }

  function applyCartDiscount(code) {
    return fetch(Shopify.routes.root + 'cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ discount: code }),
    }).then(function (r) { return r.json(); });
  }

  function addGift(variantId) {
    return fetch(Shopify.routes.root + 'cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        items: [{ id: variantId, quantity: 1, properties: { _is_gift: 'true' } }],
      }),
    }).then(function (r) { return r.json(); });
  }

  function removeGift(lineKey) {
    return fetch(Shopify.routes.root + 'cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ id: lineKey, quantity: 0 }),
    }).then(function (r) { return r.json(); });
  }

  function fetchVariantId(handle) {
    if (variantCache[handle]) return Promise.resolve(variantCache[handle]);
    return fetch(Shopify.routes.root + 'products/' + handle + '.js')
      .then(function (r) { return r.json(); })
      .then(function (data) {
        variantCache[handle] = data.variants[0].id;
        if (data.featured_image) imageCache[handle] = data.featured_image;
        else if (data.images && data.images[0]) imageCache[handle] = data.images[0];
        return variantCache[handle];
      });
  }

  function fetchProductImage(handle) {
    if (imageCache[handle]) return Promise.resolve(imageCache[handle]);
    return fetch(Shopify.routes.root + 'products/' + handle + '.js')
      .then(function (r) { return r.json(); })
      .then(function (data) {
        var img = data.featured_image || (data.images && data.images[0]) || null;
        if (img) imageCache[handle] = img;
        return img;
      })
      .catch(function () { return null; });
  }

  /* ═══════════════════════════════════════════════════════════════════════════
     DISCOUNT MANAGEMENT — NEW20 AUTO-APPLY
     Applies AUTO_COUPON to every cart that has at least one non-gift item.
     Will not overwrite a different code the customer has already entered.
     Clears automatically when the cart is emptied.
  ═══════════════════════════════════════════════════════════════════════════ */

  function isAutoCouponInCart(cart) {
    if (!cart.discount_codes) return false;
    return cart.discount_codes.some(function (d) {
      return d.code === AUTO_COUPON && d.applicable;
    });
  }

  function ensureDiscountState(cart, pairDetected) {
    var nonGiftItems = cart.items.filter(function (i) { return !isGiftItem(i); });
    var hasItems     = nonGiftItems.length > 0;
    var alreadyOn    = isAutoCouponInCart(cart);

    /* Respect a manually-entered code — don't overwrite it */
    var hasOtherCode = cart.discount_codes && cart.discount_codes.some(function (d) {
      return d.code !== AUTO_COUPON && d.applicable;
    });

    if (hasItems && !alreadyOn && !hasOtherCode) {
      /* Cart has items, no code applied yet → apply NEW20 */
      return applyCartDiscount(AUTO_COUPON).then(fetchCart);
    }

    if (!hasItems && alreadyOn) {
      /* Cart emptied but code is still set → clear it */
      return applyCartDiscount('').then(fetchCart);
    }

    return Promise.resolve(cart);
  }

  /* ═══════════════════════════════════════════════════════════════════════════
     GIFT RECONCILIATION — 1 gift with every purchase, no tier conditions
  ═══════════════════════════════════════════════════════════════════════════ */

  function reconcileGifts(passedCart) {
    if (isReconciling) return Promise.resolve({ modified: false, cart: passedCart });
    isReconciling = true;

    /* Always fetch fresh cart state before deciding — this prevents the race
       condition where a stale cart (without the gift) is passed in from
       event detail, causing reconcileGifts to add the gift a second time,
       resulting in the double-gift → remove-all → add-one loop that makes
       the gift flicker and disappear. */
    return fetchCart().then(function (cart) {

      var nonGift = cart.items.filter(function (i) { return !isGiftItem(i); });
      var gifts   = cart.items.filter(isGiftItem);
      var rawSubtotal = nonGift.reduce(function (sum, i) { return sum + i.original_line_price; }, 0);
      var target  = (nonGift.length > 0 && rawSubtotal >= MIN_PURCHASE_CENTS) ? resolveGiftVariant(cart) : null;

      if (!target) {
        if (!gifts.length) {
          isReconciling = false;
          return { modified: false, cart: cart };
        }
        var p = Promise.resolve();
        gifts.forEach(function (g) { p = p.then(function () { return removeGift(g.key); }); });
        return p.then(fetchCart).then(function (fresh) {
          isReconciling = false;
          return { modified: true, cart: fresh };
        }).catch(function (e) {
          console.error('[cart-manager] gift remove failed', e);
          isReconciling = false;
          return { modified: false, cart: cart };
        });
      }

      if (gifts.length === 1 && gifts[0].variant_id === target) {
        isReconciling = false;
        return { modified: false, cart: cart };
      }

      var chain = Promise.resolve();
      gifts.forEach(function (g) { chain = chain.then(function () { return removeGift(g.key); }); });
      chain = chain.then(function () { return addGift(target); });
      return chain.then(fetchCart).then(function (fresh) {
        isReconciling = false;
        return { modified: true, cart: fresh };
      }).catch(function (e) {
        console.error('[cart-manager] gift reconcile failed', e);
        isReconciling = false;
        return { modified: false, cart: cart };
      });

    }).catch(function (e) {
      console.error('[cart-manager] gift fetch failed', e);
      isReconciling = false;
      return { modified: false, cart: passedCart };
    });
  }

  /* ═══════════════════════════════════════════════════════════════════════════
     MAESTROO REFRESH
  ═══════════════════════════════════════════════════════════════════════════ */

  function dispatchMaestroRefresh() {
    if (refreshPending) return;
    refreshPending = true;
    document.documentElement.dispatchEvent(
      new CustomEvent('cart:refresh', { bubbles: true })
    );
  }

  /* ═══════════════════════════════════════════════════════════════════════════
     RENDER FUNCTIONS
  ═══════════════════════════════════════════════════════════════════════════ */

  // renderProgress — PAUSED (free shipping is site-wide, no tier bar needed)

  function renderGiftSlots(cart) {
    var el = document.getElementById('gift-slots-display');
    if (!el) return;

    var gifts = cart.items.filter(isGiftItem);
    if (!gifts.length) { el.innerHTML = ''; return; }

    /* Display intentionally generic (this session): we no longer reveal
       which specific product was auto-added as the gift — no image, no
       product name, no was/free price comparison. Just confirmation that
       a complimentary sample is included. Add/remove/reconciliation logic
       (addGift/removeGift/reconcileGifts) above is untouched — this only
       changes what gets rendered once a gift line item exists in the cart. */
    el.innerHTML =
      '<div class="gift-tier__slot gift-tier__slot--sample">'
        + '<span class="gift-tier__slot-icon-wrap"><span class="gift-tier__slot-icon">🎁</span></span>'
        + '<div class="gift-tier__slot-info">'
          + '<span class="gift-tier__slot-name">Muestra de regalo</span>'
          + '<span class="gift-tier__slot-label">Incluida con tu pedido</span>'
        + '</div>'
        + '<span class="gift-tier__slot-check" aria-hidden="true">'
          + '<svg viewBox="0 0 24 24" width="12" height="12" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round"><polyline points="20 6 9 17 4 12"/></svg>'
        + '</span>'
      + '</div>';
  }

  function renderManualCouponNotice(cart, manualCode, pairDetected, routine15Saving) {
    // Coupon strip UI removed — strip with ✕ close button no longer shown in drawer.
    var existing = document.getElementById('cm-manual-coupon-notice');
    if (existing) existing.remove();
    return;

    var saving;
    if (pairDetected && routine15Saving > 0 && cart.total_discount > 0) {
      var rawSubtotal   = cart.items.reduce(function (sum, item) {
        return isGiftItem(item) ? sum : sum + item.original_line_price;
      }, 0);
      var postRoutine15 = rawSubtotal - routine15Saving;
      var manualPct     = rawSubtotal > 0 ? cart.total_discount / rawSubtotal : 0;
      saving = Math.round(postRoutine15 * manualPct);
    } else {
      saving = cart.total_discount || 0;
    }
    injectStyles();

    var notice = document.createElement('div');
    notice.id  = 'cm-manual-coupon-notice';
    notice.innerHTML =
    '<div class="ue-notice">'
    + '<div class="ue-notice__left">'
    + '<span class="ue-notice__check">✓</span>'
    + '<div class="ue-notice__body">'
    + '<span class="ue-notice__code">' + esc(manualCode.toUpperCase()) + '</span>'
    + '<span class="ue-notice__desc"> applied at checkout</span>'
    + '</div>'
    + '</div>'
    + '<div style="display:flex;align-items:center;gap:.5rem;flex-shrink:0;">'
    + (saving > 0 ? '<span class="ue-notice__saving">−' + formatMoney(saving) + '</span>' : '')
    + '<button class="ue-notice__remove" type="button" aria-label="Remove coupon" title="Remove coupon">✕</button>'
    + '</div>'
    + '</div>';
    notice.querySelector('.ue-notice__remove').addEventListener('click', function () {
      applyCartDiscount('').then(fetchAndSync);
    });

    var anchor = document.getElementById('gift-slots-display');
    if (anchor && anchor.parentNode) {
      anchor.parentNode.insertBefore(notice, anchor);
    }
  }

  // renderDiscountNotice — PAUSED (ROUTINE15 not active)

  function renderSavingsBreakdown(cart, manualCode) {
    var section = document.getElementById('tsa-savings-section');
    if (!section) return;

    var nonGiftItems = cart ? cart.items.filter(function (i) { return !isGiftItem(i); }) : [];
    if (!nonGiftItems.length) { section.style.display = 'none'; return; }
    section.style.display = '';

    var rawSubtotal      = nonGiftItems.reduce(function (s, i) { return s + i.original_line_price; }, 0);
    var shippingFree     = rawSubtotal >= SHIPPING_THRESHOLD;

    // Gift row — only when gift is in cart
    var giftRow = document.getElementById('tsa-savings-gift-row');
    var hasGift = cart.items.some(isGiftItem);
    if (giftRow) giftRow.style.display = hasGift ? 'flex' : 'none';

    // Shipping — only free when cart meets the €50 threshold
    var shippingRow = document.getElementById('tsa-savings-shipping-row');
    if (shippingRow) shippingRow.style.display = shippingFree ? 'flex' : 'none';

    // Coupon row — only when a manual code is applied
    var couponRow   = document.getElementById('tsa-savings-coupon-row');
    var couponLabel = document.getElementById('tsa-savings-coupon-label');
    var couponAmt   = document.getElementById('tsa-savings-coupon-amount');
    var couponSaving = 0;
    if (couponRow) {
      if (manualCode && cart.total_discount > 0) {
        couponSaving = cart.total_discount;
        if (couponLabel) couponLabel.textContent = manualCode.toUpperCase();
        if (couponAmt)   couponAmt.textContent   = '\u2212' + formatMoney(couponSaving);

        /* −20% and (expira pronto) labels are NEW20-specific — hide for other codes */
        var isNew20 = manualCode.toUpperCase() === AUTO_COUPON.toUpperCase();
        var pctEl    = couponRow.querySelector('.tsa-savings__coupon-pct');
        var expiryEl = couponRow.querySelector('.tsa-savings__coupon-expiry');
        if (pctEl)    pctEl.style.display    = isNew20 ? '' : 'none';
        if (expiryEl) expiryEl.style.display = isNew20 ? '' : 'none';

        couponRow.style.display = 'flex';
      } else {
        couponRow.style.display = 'none';
      }
    }

    // Total Ahorrado = gift value + shipping saving (only when qualified) + coupon saving
    var totalSaving = (hasGift ? 1000 : 0) + (shippingFree ? SHIPPING_CENTS : 0) + couponSaving;
    var totalEl = document.getElementById('cart-savings-display');
    if (totalEl) totalEl.textContent = '\u2212' + formatMoney(totalSaving);

    // Estimated shipping row — visible when cart is below the free-shipping threshold
    var shippingEstRow = document.getElementById('tsa-savings-shipping-est-row');
    if (shippingEstRow) shippingEstRow.style.display = shippingFree ? 'none' : 'flex';

    // Total a pagar — adds estimated shipping when below threshold so the customer
    // knows the real amount they will pay at checkout (cart API never includes shipping)
    var payableEl   = document.getElementById('tsa-savings-payable');
    var payableHint = document.getElementById('tsa-payable-hint');
    var payableTotal = cart.total_price + (shippingFree ? 0 : SHIPPING_CENTS);
    if (payableEl)   payableEl.textContent = formatMoney(payableTotal);
    if (payableHint) payableHint.style.display = shippingFree ? 'none' : 'inline';
  }

  function renderTotals(cart, pairDetected, routine15Saving) {
    var totalEl    = document.getElementById('cart-total-display');
    var labelEl    = document.getElementById('cart-total-label');
    var savingsRow = document.getElementById('cart-savings-row');
    var savingsEl  = document.getElementById('cart-savings-display');

    var rawSubtotal     = cart.items.reduce(function (sum, item) {
      return isGiftItem(item) ? sum : sum + item.original_line_price;
    }, 0);
    var routine15InCart = isRoutine15InCart(cart);
    var manualCode      = getManualCode(cart);
    var postRoutine15   = rawSubtotal - routine15Saving;

    var displayedSub;
    var totalSaving = routine15Saving;

    if (routine15InCart) {
      displayedSub = cart.total_price;
    } else if (pairDetected && manualCode && cart.total_discount > 0 && rawSubtotal > 0) {
      var manualPct    = cart.total_discount / rawSubtotal;
      var manualSaving = Math.round(postRoutine15 * manualPct);
      displayedSub = postRoutine15 - manualSaving;
      totalSaving  = routine15Saving + manualSaving;
    } else if (pairDetected) {
      displayedSub = postRoutine15;
    } else if (manualCode && cart.total_discount > 0) {
      displayedSub = cart.total_price;
      totalSaving  = cart.total_discount;
    } else {
      displayedSub = cart.total_price;
    }

    // Add gift savings (retail value of free gift items via compare_at_price)
    var giftSavings = 0;
    cart.items.forEach(function (item) {
      if (isGiftItem(item)) giftSavings += item.original_price * item.quantity;
    });
    totalSaving += giftSavings;

    var displayTotal = displayedSub + (rawSubtotal >= SHIPPING_THRESHOLD ? 0 : SHIPPING_CENTS);

    if (totalEl) totalEl.textContent = formatMoney(displayTotal);
    var checkoutBtn = document.getElementById('tsa-checkout-full-btn');
    if (checkoutBtn) checkoutBtn.setAttribute('aria-label', 'Finalizar compra — ' + formatMoney(displayTotal));

    if (savingsRow && savingsEl) {
      if (totalSaving > 0) {
        savingsEl.textContent = '−' + formatMoney(totalSaving);
        savingsRow.style.display = 'flex';
      } else {
        savingsRow.style.display = 'none';
      }
    }
  }

  /* ═══════════════════════════════════════════════════════════════════════════
     MASTER SYNC
  ═══════════════════════════════════════════════════════════════════════════ */

  /* ═══════════════════════════════════════════════════════════════════════════
     TRAVEL MINI RECONCILIATION
     Enforces the per-order cap (max 2, or the customer's remaining lifetime
     allowance if window._tsaMiniAllowance is set by product-info.liquid).
     Runs after gift reconciliation in every sync cycle.
  ═══════════════════════════════════════════════════════════════════════════ */

  function getMiniMax() {
    /* Read lifetime-adjusted allowance set by product-info.liquid on PDPs.
       Falls back to 2 on non-PDP pages (per-order max). */
    return typeof window._tsaMiniAllowance !== 'undefined'
      ? Math.max(0, parseInt(window._tsaMiniAllowance, 10))
      : 1;
  }

  function reconcileMinis(giftResult) {
    var cart = giftResult.cart;
    var max  = getMiniMax();

    var minis = (cart.items || []).filter(function (i) {
      return i.properties && i.properties['_travel_mini'] === '1';
    });

    if (minis.length <= max) {
      return Promise.resolve({ modified: giftResult.modified, cart: cart });
    }

    /* Remove excess minis (keep the first `max`, remove the rest) */
    var excess = minis.slice(max);
    var chain  = Promise.resolve();
    excess.forEach(function (item) {
      chain = chain.then(function () {
        return fetch('/cart/change.js', {
          method:  'POST',
          headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
          body:    JSON.stringify({ id: item.key, quantity: 0 })
        }).then(function (r) { return r.json(); });
      });
    });

    return chain.then(fetchCart).then(function (fresh) {
      return { modified: true, cart: fresh };
    }).catch(function (e) {
      console.error('[cart-manager] mini reconcile failed', e);
      return { modified: giftResult.modified, cart: cart };
    });
  }

  function renderProgressBar(cart) {
    var wrap  = document.getElementById('tsa-progress-wrap');
    if (!wrap) return;

    var nonGift     = cart ? cart.items.filter(function (i) { return !isGiftItem(i); }) : [];
    var rawSubtotal = nonGift.reduce(function (s, i) { return s + i.original_line_price; }, 0);

    if (!cart || !nonGift.length) {
      wrap.style.display = 'none';
      return;
    }

    wrap.style.display = '';

    var pct      = Math.min(100, (rawSubtotal / MIN_PURCHASE_CENTS) * 100);
    var fillEl   = document.getElementById('tsa-progress-fill');
    var msgEl    = document.getElementById('tsa-progress-msg');
    var unlocked = rawSubtotal >= MIN_PURCHASE_CENTS;

    if (fillEl) fillEl.style.width = pct + '%';
    wrap.classList.toggle('is-unlocked', unlocked);

    if (msgEl) {
      if (unlocked) {
        msgEl.innerHTML = '<span class="tsa-progress__check">✓</span> ¡Regalo gratis + envío gratis desbloqueados!';
      } else {
        var remaining   = MIN_PURCHASE_CENTS - rawSubtotal;
        var euros       = Math.floor(remaining / 100);
        var cents       = remaining % 100;
        var remainAmt   = euros + (cents ? ',' + (cents < 10 ? '0' + cents : cents) : '') + '€';
        msgEl.innerHTML = 'Añade <strong>' + remainAmt + '</strong> más para 🎁 regalo + 🚚 envío gratis';
      }
    }
  }

  function renderAll(cart) {
    if (!cart || !cart.item_count) {
      // Empty cart — clear everything
      var slotsEl        = document.getElementById('gift-slots-display');
      var upsellEl       = document.getElementById('upsell-display');
      var noticeEl       = document.getElementById('cm-routine15-notice');
      var manualNoticeEl = document.getElementById('cm-manual-coupon-notice');
      var savingsEl      = document.getElementById('tsa-savings-section');
      var progressEl     = document.getElementById('tsa-progress-wrap');
      if (slotsEl)        slotsEl.innerHTML  = '';
      if (upsellEl)       upsellEl.innerHTML = '';
      if (noticeEl)       noticeEl.remove();
      if (manualNoticeEl) manualNoticeEl.remove();
      if (savingsEl)      savingsEl.style.display = 'none';
      if (progressEl)     progressEl.style.display = 'none';
      return;
    }

    var pairDetected    = detectPair(cart);
    var routine15Saving = pairDetected ? calcRoutine15Saving(cart) : 0;
    var manualCode      = getManualCode(cart);

    renderProgressBar(cart);
    renderGiftSlots(cart);
    renderManualCouponNotice(cart, manualCode, pairDetected, routine15Saving);
    renderSavingsBreakdown(cart, manualCode);
    renderTotals(cart, pairDetected, routine15Saving);
  }

  /**
   * Full sync cycle:
   * 1. Fetch cart
   * 2. ensureDiscountState — auto-apply / remove NEW20
   * 3. Reconcile gifts — always 1 gift when cart has non-gift items
   * 4. If gifts changed → dispatch Maestroo refresh
   * 5. Else → render directly
   */
  /* True only when the current document IS the standalone Cart page —
     used to decide how to reflect an added/removed line-item row
     (gift or travel mini) after reconciliation. */
  function isOnCartPage() {
    var p = window.location.pathname;
    return p === '/cart' || p === (Shopify.routes.root + 'cart') || p === (Shopify.routes.root + 'cart/');
  }

  function runSync(cart) {
    ensureDiscountState(cart, detectPair(cart))
      .then(function (freshCart) {
        return reconcileGifts(freshCart);
      })
      .then(function (giftResult) {
        return reconcileMinis(giftResult);
      })
      .then(function (result) {
        if (result.modified) {
          /* A gift or travel-mini row was added/removed server-side.
             That requires a new/removed <line-item> row, which renderAll()
             cannot produce (it only repaints the progress bar, gift-slot
             widget, savings breakdown, and totals — never the item rows
             themselves). dispatchMaestroRefresh() normally handles this by
             asking Maestroo's CartDrawer to redraw and echo back
             'cart-drawer:refreshed' — but that drawer doesn't exist on the
             standalone Cart page, so that echo never arrives and renderAll()
             silently never runs, leaving the gift row and every downstream
             calculation stuck until a manual refresh.
             On the Cart page specifically, reload instead — it's the only
             way to get a correct new line-item row without duplicating
             Liquid's rendering logic in JS. Everywhere else (drawer context),
             keep the existing in-place refresh. */
          if (isOnCartPage()) {
            window.location.reload();
          } else {
            dispatchMaestroRefresh();
          }
        } else {
          renderAll(result.cart);
        }
      })
      .catch(function (e) {
        console.error('[cart-manager] sync failed', e);
      });
  }

  function fetchAndSync() {
    fetchCart()
      .then(runSync)
      .catch(function (e) { console.error('[cart-manager] fetch failed', e); });
  }

  // Expose for coupon apply in cart-drawer.liquid
  window._cartManager = { fetchAndSync: fetchAndSync };

  /* ═══════════════════════════════════════════════════════════════════════════
     EVENT BINDING
  ═══════════════════════════════════════════════════════════════════════════ */

  function bindUpsellAddEvent(container, handle) {
    var btn = container.querySelector('.ue-add-btn');
    if (!btn) return;
    btn.addEventListener('click', function () {
      var self = this;
      if (self.disabled) return;
      self.disabled    = true;
      self.textContent = 'Adding...';

      fetchVariantId(handle)
        .then(function (variantId) {
          if (!variantId) throw new Error('no variant id');
          return fetch(Shopify.routes.root + 'cart/add.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ id: variantId, quantity: 1 }),
          });
        })
        .then(fetchCart)
        .then(function (cart) {
          runSync(cart);
        })
        .catch(function () {
          self.textContent = 'Error — try again';
          self.disabled    = false;
        });
    });
  }

  /** ROUTINE15 paused — checkout always goes straight to /checkout */
  function bindCheckoutInterception() {
  document.addEventListener('click', function (e) {
    var btn = e.target.closest('button[name="checkout"]');
    if (!btn || !btn.closest('#cart-drawer')) return;

    // If cart is empty, let Maestroo handle it natively
    if (!btn.closest('form') || btn.disabled) return;

    e.preventDefault();
    e.stopImmediatePropagation();

    fetchCart().then(function (cart) {
      // Guard: if cart is empty after checkout completion, just go to checkout natively
      if (!cart.item_count) {
        window.location.href = '/checkout';
        return;
      }
      window.location.href = '/checkout';
    }).catch(function () {
      window.location.href = '/checkout';
    });
  }, true);
}

  /* ═══════════════════════════════════════════════════════════════════════════
     MAESTROO EVENT LISTENERS
  ═══════════════════════════════════════════════════════════════════════════ */

  /* Shared handler — the drawer fires 'cart:change' on add/remove/quantity;
     the standalone Cart page's native <line-item> remove button and quantity
     selector instead fire 'cart:updated' for the same actions (confirmed by
     the skin-switcher's own re-init listener further down in line-item.liquid).
     Both must run the full reconcile chain — without this, removing an item
     or changing quantity on /cart updates the DOM row but leaves gifts,
     shipping progress, and savings stale until a hard refresh.

     Debounced: Maestroo can fire these events twice in quick succession for
     a single user action (an optimistic pre-fire + a confirmation fire).
     Coalescing avoids two overlapping reconcile cycles racing each other. */
  var cartEventDebounce = null;
  function handleMaestrooCartEvent(event) {
    var cart = event.detail && event.detail.cart;

    clearTimeout(cartEventDebounce);
    cartEventDebounce = setTimeout(function () {
      if (cart) {
        // Render immediately so gift slots etc. are visible
        // the moment the drawer opens — reconciliation may update again shortly after
        renderAll(cart);
        runSync(cart);
      } else {
        fetchAndSync();
      }
    }, 120);
  }

  document.addEventListener('cart:change',  handleMaestrooCartEvent);
  document.addEventListener('cart:updated', handleMaestrooCartEvent);

  window.addEventListener('pageshow', function (event) {
    if (event.persisted) {
      isReconciling  = false;
      refreshPending = false;
      fetchCart().then(function (cart) {
        document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
      }).catch(function () {
        document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
      });
    }
  });

  var refreshedTimer = null;
  document.addEventListener('cart-drawer:refreshed', function () {
    var wasOurs = refreshPending;
    refreshPending = false;
    clearTimeout(refreshedTimer);
    refreshedTimer = setTimeout(function () {
      if (wasOurs) {
        fetchCart().then(renderAll).catch(function () {});
      } else {
        fetchAndSync();
      }
    }, 0);
  });

  /* ═══════════════════════════════════════════════════════════════════════════
     PDP UPSELL — PAUSED
     initPdpUpsell kept in file for easy re-enable; returns early.
  ═══════════════════════════════════════════════════════════════════════════ */

  function initPdpUpsell() {
    // PAUSED — upsell card hidden site-wide
    return;
  }

  /* ═══════════════════════════════════════════════════════════════════════════
     INIT
  ═══════════════════════════════════════════════════════════════════════════ */

  bindCheckoutInterception();

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      fetchAndSync();
      initPdpUpsell();
    });
  } else {
    fetchAndSync();
    initPdpUpsell();
  }

}());